import { useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, CircularProgress, Alert, IconButton } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment, { type Moment } from "moment";
import TabsComponent from "../../components/NewTabs";
import { getDecodeURI, getEncodedURI } from "../../utils/util";
import { DataProvider, useDataCtx } from "./context/DataContext";
import CommonFilters from "../../components/BreadCrumbs/CommonFilters";
import { updatePermission, updateSideMenu } from "../../actions/Users/authenticate";
import { SIDE_MENU_ITEM_DATA_NAVIGATOR, DATA_NAVIGATOR_ALLOWED_OU_IDS } from "../../utils/constants";
import Overview from "./pages/Overview";
import DailySummaries from "./pages/DailySummaries";
import Heatmap from "./pages/Heatmap";
import UsageTrend from "./pages/UsageTrend";
import ShipAnalysis from "./pages/ShipAnalysis";
import Alerts from "./pages/Alerts";

const TABS = [
  { tabValue: "overview", tabName: "Overview" },
  { tabValue: "daily", tabName: "Daily Summaries" },
  { tabValue: "heatmap", tabName: "Ship Heatmap" },
  { tabValue: "trend", tabName: "Usage Trend" },
  { tabValue: "ships", tabName: "Ship Analysis" },
  { tabValue: "alerts", tabName: "Alerts" },
];

const DataNavigatorContent = (props: any) => {
  const { match, location, history } = props;
  const { period, isLoading, error, notFoundForDate, loadFile, loadFromDate, clearData, hasData, data } = useDataCtx();
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
  const dispatch = useDispatch();
  const selectedOu = useSelector((state: any) => state.authReducer?.selectedOu);
  const selectedOuId = selectedOu?.id;

  const activeTab = match?.params?.tab || "overview";
  const _q = getDecodeURI(location?.search);
  const [selectedVessel, setSelectedVessel] = useState<string | null>(_q.vessel || null);

  useEffect(() => {
    (dispatch as any)(updatePermission({ isMultiVessel: false, isSingleVessel: false, showOu: true }));
  }, [dispatch]);

  const authReducer = useSelector((state: any) => state.authReducer);
  useEffect(() => {
    if (!authReducer?.sideMenu || authReducer.sideMenu.menuItem !== SIDE_MENU_ITEM_DATA_NAVIGATOR) {
      (dispatch as any)(updateSideMenu({ menuItem: SIDE_MENU_ITEM_DATA_NAVIGATOR }));
    }
  }, [dispatch, authReducer?.sideMenu]);

  useEffect(() => {
    if (selectedOuId && !DATA_NAVIGATOR_ALLOWED_OU_IDS.includes(selectedOuId)) {
      const params = getDecodeURI(location?.search);
      if (selectedOu?.name) params.ouName = selectedOu.name;
      if (selectedOu?.id) params.ouId = selectedOu.id;
      history.push({ pathname: "/fleet-dashboard/sites", search: `?${getEncodedURI(params)}` });
    }
  }, [selectedOuId, selectedOu, history, location]);

  useEffect(() => {
    const lastRow = data?.trendData?.[data.trendData.length - 1];
    if (lastRow?.date) {
      setSelectedDate(moment(lastRow.date, "YYYY-MM-DD"));
    }
  }, [data]);

  const navigateToTab = (pathname: string, tabValue: string) => {
    history.push({ pathname: `/data-navigator/${tabValue}`, search: location.search });
  };

  const goToVessel = (vessel: string) => {
    setSelectedVessel(vessel);
    const params = getDecodeURI(location?.search);
    params.vessel = vessel;
    history.push({ pathname: "/data-navigator/ships", search: `?${getEncodedURI(params)}` });
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await loadFile(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleDateChange = async (m: Moment | null) => {
    setSelectedDate(m);
    if (m && m.isValid()) {
      await loadFromDate(m.format("YYYY-MM-DD"));
    }
  };

  const shiftDate = async (days: number) => {
    const base = selectedDate ?? moment();
    const next = base.clone().add(days, "day");
    await handleDateChange(next);
  };

  const fleetName = "Torm";

  const downloadButtonSx = {
    backgroundColor: "rgba(36,76,132,1)",
    color: "#fff",
    fontWeight: 700,
    textTransform: "uppercase",
    borderRadius: "8px",
    boxShadow: "none",
    px: 2.5,
    py: 1,
    "&:hover": { backgroundColor: "rgba(28,60,105,1)", boxShadow: "none" },
    "&.Mui-disabled": { backgroundColor: "rgba(36,76,132,0.5)", color: "#fff" },
  };

  return (
    <Box className="Form Data-Navigator-Container" sx={{ height: "100vh", width: "100%", maxWidth: "100%", display: "flex", flexDirection: "column", overflowX: "hidden" }}>
      <Box sx={{ flexShrink: 0, marginTop: "49px", minHeight: "62px", position: "relative", zIndex: 1298 }}>
        <CommonFilters showWantType={false} showSummaryPeriod={false} disableSite />
      </Box>

      {error && <Alert severity="error" sx={{ mx: 2, mt: 1, flexShrink: 0 }}>{error}</Alert>}

      <Box sx={{ flexShrink: 0 }}>
        <TabsComponent tabs={TABS} handleChangeTab={navigateToTab} activeTab={activeTab} removemarginTop />
      </Box>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        flexWrap="wrap"
        gap={1}
        sx={{ px: 2, py: 1, borderBottom: "1px solid", borderColor: "divider", flexShrink: 0, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}
      >
        {hasData && (
          <div style={{ marginRight: "auto", minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 500, color: "#666", whiteSpace: "nowrap" }}>{fleetName} Usage Dashboard</div>
            {period && <div style={{ fontSize: 12, color: "#999" }}>{period}</div>}
          </div>
        )}
        {false && (
          <>
            <IconButton size="small" onClick={() => shiftDate(-1)} disabled={isLoading} title="Previous day">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Report date"
                value={selectedDate}
                onChange={handleDateChange}
                disabled={isLoading}
                disableFuture
                slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
              />
            </LocalizationProvider>
            <IconButton size="small" onClick={() => shiftDate(1)} disabled={isLoading} title="Next day">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </>
        )}
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={handleFile} />
        <Button
          size="small"
          variant="contained"
          disableElevation
          startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <UploadFileIcon />}
          onClick={() => fileRef.current?.click()}
          disabled={isLoading}
          sx={downloadButtonSx}
        >
          {isLoading ? "Processing…" : hasData ? "Replace file" : "Upload Excel / CSV"}
        </Button>
        {hasData && (
          <Button size="small" variant="text" startIcon={<CloseIcon />} onClick={() => { clearData(); setSelectedDate(null); }}>
            Clear
          </Button>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {isLoading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={2}
            sx={{ height: "100%" }}
          >
            <CircularProgress />
            <Box component="p" sx={{ fontSize: 14, color: "text.secondary", m: 0 }}>Loading data…</Box>
          </Box>
        ) : !hasData ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={2}
            sx={{ height: "100%", textAlign: "center", px: 2 }}
          >
            <UploadFileIcon sx={{ fontSize: 48, color: "text.disabled" }} />
            <Box>
              <Box component="p" sx={{ fontSize: 18, fontWeight: 600, m: 0 }}>
                {notFoundForDate ? `No report available for ${notFoundForDate}` : "No data loaded yet"}
              </Box>
              <Box component="p" sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
                {notFoundForDate
                  ? "Try a different date, or upload an Excel/CSV file directly."
                  : "Pick a report date above, or upload an Excel/CSV file (Date, Site, Users, WAN, Auth, Static, UnAuth) to view the dashboards."}
              </Box>
            </Box>
            <Button
              variant="contained"
              disableElevation
              startIcon={<UploadFileIcon />}
              onClick={() => fileRef.current?.click()}
              sx={downloadButtonSx}
            >
              Upload Excel / CSV
            </Button>
          </Box>
        ) : (
          <>
            {activeTab === "overview" && <Overview onNavigate={(tab) => navigateToTab("", tab)} />}
            {activeTab === "daily" && <DailySummaries onNavigateToVessel={goToVessel} />}
            {activeTab === "heatmap" && <Heatmap onNavigateToVessel={goToVessel} />}
            {activeTab === "trend" && <UsageTrend />}
            {activeTab === "ships" && (
              <ShipAnalysis
                selectedVessel={selectedVessel}
                onSelectVessel={(v) => (v ? goToVessel(v) : setSelectedVessel(null))}
                onBackToFleet={() => setSelectedVessel(null)}
              />
            )}
            {activeTab === "alerts" && <Alerts onNavigateToVessel={goToVessel} />}
          </>
        )}
      </Box>
    </Box>
  );
};

const DataNavigator = (props: any) => (
  <DataProvider>
    <DataNavigatorContent {...props} />
  </DataProvider>
);

export default withRouter(DataNavigator);
